import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';  // Fixed: changed from '../../hooks/useApi'
import LoadingSpinner from '../components/Common/LoadingSpinner';  // Also fix this if needed
import { 
  UsersIcon, 
  TruckIcon, 
  MapIcon, 
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalDrivers: 0,
    activeDrivers: 0,
    totalVehicles: 0,
    activeVehicles: 0,
    totalTrips: 0,
    activeTrips: 0,
    totalDevices: 0,
    activeDevices: 0,
    recentEvents: [],
    recentTrips: []
  });
  
  const [loading, setLoading] = useState(true);
  const { get } = useApi();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    
    try {
      // Fetch drivers stats
      const driversRes = await get('/api/drivers/filter?size=1');
      const activeDriversRes = await get('/api/drivers/filter?active=true&size=1');
      
      // Fetch vehicles stats
      const vehiclesRes = await get('/api/vehicles/filter?size=1');
      const activeVehiclesRes = await get('/api/vehicles/filter?active=true&size=1');
      
      // Fetch trips stats
      const tripsRes = await get('/api/trips/filter?size=1');
      const activeTripsRes = await get('/api/trips/filter?endedAt=null&size=1');
      
      // Fetch devices stats
      const devicesRes = await get('/api/devices/filter?size=1');
      const activeDevicesRes = await get('/api/devices/filter?active=true&size=1');
      
      // Fetch recent events
      const eventsRes = await get('/api/events/filter?size=5&sort=timestamp,desc');
      
      // Fetch recent trips
      const recentTripsRes = await get('/api/trips/filter?size=5&sort=startedAt,desc');

      setStats({
        totalDrivers: driversRes.data?.totalElements || 0,
        activeDrivers: activeDriversRes.data?.totalElements || 0,
        totalVehicles: vehiclesRes.data?.totalElements || 0,
        activeVehicles: activeVehiclesRes.data?.totalElements || 0,
        totalTrips: tripsRes.data?.totalElements || 0,
        activeTrips: activeTripsRes.data?.totalElements || 0,
        totalDevices: devicesRes.data?.totalElements || 0,
        activeDevices: activeDevicesRes.data?.totalElements || 0,
        recentEvents: eventsRes.data?.content || [],
        recentTrips: recentTripsRes.data?.content || []
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const StatCard = ({ title, value, icon: Icon, color, subValue, subLabel }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
          {subValue !== undefined && (
            <p className="text-sm text-gray-600 mt-1">
              {subLabel}: <span className="font-semibold">{subValue}</span>
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Drivers"
          value={stats.totalDrivers}
          icon={UsersIcon}
          color="bg-blue-500"
          subValue={stats.activeDrivers}
          subLabel="Active"
        />
        
        <StatCard
          title="Total Vehicles"
          value={stats.totalVehicles}
          icon={TruckIcon}
          color="bg-green-500"
          subValue={stats.activeVehicles}
          subLabel="Active"
        />
        
        <StatCard
          title="Total Trips"
          value={stats.totalTrips}
          icon={MapIcon}
          color="bg-purple-500"
          subValue={stats.activeTrips}
          subLabel="In Progress"
        />
        
        <StatCard
          title="Total Devices"
          value={stats.totalDevices}
          icon={DevicePhoneMobileIcon}
          color="bg-orange-500"
          subValue={stats.activeDevices}
          subLabel="Active"
        />
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Events */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Recent Events</h2>
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
          </div>
          
          {stats.recentEvents.length > 0 ? (
            <div className="space-y-4">
              {stats.recentEvents.map((event) => (
                <div key={event.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 mt-2 rounded-full bg-yellow-500"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {event.eventType?.description || 'Unknown Event'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Trip #{event.tripId} • {formatDate(event.timestamp)}
                    </p>
                    {event.voiceText && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {event.voiceText}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent events</p>
          )}
        </div>

        {/* Recent Trips */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Recent Trips</h2>
            <CalendarIcon className="h-5 w-5 text-indigo-500" />
          </div>
          
          {stats.recentTrips.length > 0 ? (
            <div className="space-y-4">
              {stats.recentTrips.map((trip) => (
                <div key={trip.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0">
                    <div className={`w-2 h-2 mt-2 rounded-full ${
                      trip.endedAt ? 'bg-green-500' : 'bg-blue-500 animate-pulse'
                    }`}></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {trip.label || `Trip #${trip.id}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Driver: {trip.driver?.fullName || 'Unknown'} • 
                      Vehicle: {trip.vehicle?.plates || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Started: {formatDate(trip.startedAt)}
                      {trip.endedAt && ` • Ended: ${formatDate(trip.endedAt)}`}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      trip.endedAt 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {trip.endedAt ? 'Completed' : 'In Progress'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent trips</p>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Active Drivers</h3>
          <p className="text-3xl font-bold">{stats.activeDrivers}</p>
          <p className="text-sm opacity-90 mt-2">
            {((stats.activeDrivers / stats.totalDrivers) * 100 || 0).toFixed(1)}% of total drivers
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Active Vehicles</h3>
          <p className="text-3xl font-bold">{stats.activeVehicles}</p>
          <p className="text-sm opacity-90 mt-2">
            {((stats.activeVehicles / stats.totalVehicles) * 100 || 0).toFixed(1)}% of total vehicles
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Active Devices</h3>
          <p className="text-3xl font-bold">{stats.activeDevices}</p>
          <p className="text-sm opacity-90 mt-2">
            {((stats.activeDevices / stats.totalDevices) * 100 || 0).toFixed(1)}% of total devices
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;